import { useEffect, useRef, useState } from "react";
/** A reply created up to this long before the HUD mounted still counts as arriving (clock skew, slow first render). */
const ARRIVAL_GRACE_MS = 10000;
/** Timestamps below this are in seconds, not milliseconds. */
const SECONDS_THRESHOLD = 1e11;
const MS_PER_SECOND = 1000;
const toMs = (createdAt) => {
    const time = createdAt instanceof Date ? createdAt.getTime() : createdAt;
    return time < SECONDS_THRESHOLD ? time * MS_PER_SECOND : time;
};
/**
 * Id of the displayed snapshot's message when it just arrived — the only case the strip animates its deltas.
 * A snapshot counts as arrived the first time it is shown, if it is the live state and its message was created
 * after the HUD mounted. History (including a turn-0 placeholder that is replaced once history loads), switching
 * back to an earlier reply and returning from a past state never animate, and no message animates twice.
 * Messages already loaded when the HUD starts (or resumes) showing snapshots count as shown, so an inactive
 * placement that becomes active on a breakpoint change does not replay the last delta, and an arrival is
 * forgotten once the snapshot disappears so a placement that goes inactive and resumes does not replay it either.
 */
export default function useArrivedSnapshotId({ snapshot, conversation, altMsgs, }) {
    const refMountedAt = useRef(Date.now());
    const refShownIds = useRef(new Set());
    const refWasShowing = useRef(false);
    const [stateArrivedId, setStateArrivedId] = useState(null);
    const messageId = snapshot?.messageId;
    const isPastState = snapshot?.isPastState ?? false;
    useEffect(() => {
        if (!messageId) {
            refWasShowing.current = false;
            setStateArrivedId(null);
            return;
        }
        if (!refWasShowing.current) {
            refWasShowing.current = true;
            for (const entry of conversation) {
                refShownIds.current.add(entry.id);
            }
            for (const entry of altMsgs) {
                refShownIds.current.add(entry.id);
            }
        }
        if (refShownIds.current.has(messageId)) {
            setStateArrivedId((previous) => (!isPastState && previous === messageId ? previous : null));
            return;
        }
        refShownIds.current.add(messageId);
        const message = conversation.find((entry) => entry.id === messageId) ?? altMsgs.find((entry) => entry.id === messageId);
        const hasArrived = !isPastState && message !== undefined && toMs(message.createdAt) >= refMountedAt.current - ARRIVAL_GRACE_MS;
        setStateArrivedId(hasArrived ? messageId : null);
    }, [messageId, isPastState, conversation, altMsgs]);
    return stateArrivedId;
}
